import React, { useMemo } from "react";

import styles from "./ClientCSVTips.module.css";

interface Props {
  seeMore: boolean;
}

const Tips = (props: Props): JSX.Element => {
  const PartialTips = useMemo(() => {
    return (
      <>
        <li>
          Account Number (Required) = Custodian account number, with no hypens
        </li>
        <li>Advisor (Required) = Full name of the client's advisor</li>
        <li>
          Client 1 Title (Optional) = Prefix name of the primary account holder.
          Valid options include:
          <div className={styles.listWrapped}>
            <div>• Mr</div>
            <div>• Dr</div>
            <div>• Mdm</div>
            <div>• Miss</div>
            <div>• Mrs</div>
            <div>• Ms</div>
            <div>• Prof</div>
            <div>• Sir</div>
          </div>
        </li>
      </>
    );
  }, []);

  if (props.seeMore === false) {
    return (
      <ul className={styles.list}>
        <li>What to put in the columns</li>
        <ul className={styles.list}>{PartialTips}</ul>
      </ul>
    );
  }

  return (
    <ul className={styles.list}>
      <li>
        What to put in the columns
        <ul className={styles.list}>
          {PartialTips}
          <li>
            Client 1 First Name (Required) = First name of the primary account
            holder.
          </li>
          <li>
            Client 1 Middle Name (Optional) = Middle name of the primary account
            holder.
          </li>
          <li>
            Client 1 Last Name (Required) = Last name of the primary account
            holder.
          </li>
          <li>
            Client 1 Suffix (Optional) = Suffix name of the primary account
            holder. Valid options include:
            <div className={styles.listWrapped}>
              <div>• II</div>
              <div>• III</div>
              <div>• IV</div>
              <div>• Jr</div>
              <div>• Sr</div>
            </div>
          </li>
          <li>
            Client 1 Email Address (Required) = Email Address of the primary
            account holder.
          </li>
          <li>
            Client 2 Title (Optional) = Prefix name of the secondary account
            holder. Valid options include:
            <div className={styles.listWrapped}>
              <div>• Mr</div>
              <div>• Dr</div>
              <div>• Mdm</div>
              <div>• Miss</div>
              <div>• Mrs</div>
              <div>• Ms</div>
              <div>• Prof</div>
              <div>• Sir</div>
            </div>
          </li>
          <li>
            Client 2 First Name (Optional) = First name of the secondary account
            holder.
          </li>
          <li>
            Client 2 Middle Name (Optional) = Middle name of the secondary
            account holder.
          </li>
          <li>
            Client 2 Last Name (Optional) = Last name of the secondary account
            holder.
          </li>
          <li>
            Client 2 Suffix (Optional) = Suffix name of the secondary account
            holder. Valid options include:
            <div className={styles.listWrapped}>
              <div>• II</div>
              <div>• III</div>
              <div>• IV</div>
              <div>• Jr</div>
              <div>• Sr</div>
            </div>
          </li>
          <li>
            Client 2 Email Address (Optional) = Email Address of the secondary
            account holder.
          </li>
          <li>
            Account Name (Optional) = Custom name of the client's account e.g.
            "My Savings Account" (a placeholder account name will be created if
            this is not specified)
          </li>
          <li>
            Account Type (Required) = The account category. Valid options
            include:
            <div className={styles.listWrapped}>
              <div>• Asset Lending</div>
              <div>• Individual</div>
              <div>• IRA</div>
              <div>• Inherited IRA</div>
              <div>• Inherited Roth IRA</div>
              <div>• Joint CP</div>
              <div>• Joint TE</div>
              <div>• Joint TIC</div>
              <div>• Joint WROS</div>
              <div>• Rollover IRA</div>
              <div>• Roth IRA</div>
              <div>• SEP IRA</div>
              <div>• Trust</div>
              <div>• Other</div>
            </div>
          </li>
          <li>
            Custodian (Required) = The financial institution where the account
            was established. Valid options include:
            <div className={styles.listWrapped}>
              <div>• Apex</div>
              <div>• DPL</div>
              <div>• Fidelity</div>
              <div>• Pershing</div>
              <div>• Pontera</div>
              <div>• Schwab</div>
            </div>
          </li>
          <li>
            Client 1 Gender (Optional) = Gender of the primary account holder.
            Valid options include:
            <div className={styles.listWrapped}>
              <div>• Male</div>
              <div>• Female</div>
              <div>• Non-binary</div>
              <div>• Undisclosed</div>
            </div>
          </li>
          <li>
            Client 1 Marital Status (Optional) = Marital status of the primary
            account holder. Valid options include:
            <div className={styles.listWrapped}>
              <div>• Single</div>
              <div>• Married</div>
            </div>
          </li>
          <li>
            Client 1 Phone Number (Optional) = Phone number of the primary
            account holder. Expected format: "(###) ###-####", for example
            "(987) 123-4567"
          </li>
          <li>
            Client 1 Date of Birth (Optional) = The primary account holder's
            birthdate in MM/DD/YYYY format
          </li>
          <li>
            Client 1 Address: Street (Optional) = Street name. Note: other
            address details will be ignored if this value is missing
          </li>
          <li>Client 1 Address: Unit (Optional) = Unit name</li>
          <li>
            Client 1 Address: City (Optional) = City name. Note: other address
            details will be ignored if this value is missing
          </li>
          <li>
            Client 1 Address: State (Optional) = State code, for example: "NJ",
            "NY", "LA". Note: other address details will be ignored if this
            value is missing
          </li>
          <li>
            Client 1 Address: Postal Code (Optional) = Postal Code. Note: other
            address details will be ignored if this value is missing
          </li>
          <li>
            Client 1 Address: Country Code (Optional) = Country code, for
            example "US". Note: other address details will be ignored if this
            value is missing.
          </li>
          <li>
            Client 1 Tax Identification Number Type (Optional) = Tax
            identification type code. Note: other tax identification details
            will be ignored if this value is missing. Valid options include:
            <div className={styles.listWrapped}>
              <div>• SSN</div>
              <div>• ITIN</div>
              <div>• EIN</div>
            </div>
          </li>
          <li>
            Client 1 Tax Identification Number (Optional) = Example:
            "030-88-6000". Note: other tax identification details will be
            ignored if this value is missing
          </li>
          <li>
            Client 2 Gender (Optional) = Gender of the secondary account holder.
            Valid options include:
            <div className={styles.listWrapped}>
              <div>• Male</div>
              <div>• Female</div>
              <div>• Non-binary</div>
              <div>• Undisclosed</div>
            </div>
          </li>
          <li>
            Client 2 Marital Status (Optional) = Marital status of the secondary
            account holder. Valid options include:
            <div className={styles.listWrapped}>
              <div>• Single</div>
              <div>• Married</div>
            </div>
          </li>
          <li>
            Client 2 Phone Number (Optional) = Phone number of the secondary
            account holder. Expected format: "(###) ###-####", for example
            "(987) 123-4567"
          </li>
          <li>
            Client 2 Date of Birth (Optional) = The secondary account holder's
            birthdate in MM/DD/YYYY format
          </li>
          <li>
            Client 2 Address: Street (Optional) = Street name. Note: other
            address details will be ignored if this value is missing
          </li>
          <li>Client 2 Address: Unit (Optional) = Unit name</li>
          <li>
            Client 2 Address: City (Optional) = City name. Note: other address
            details will be ignored if this value is missing
          </li>
          <li>
            Client 2 Address: State (Optional) = State code, for example: "NJ",
            "NY", "LA". Note: other address details will be ignored if this
            value is missing
          </li>
          <li>
            Client 2 Address: Postal Code (Optional) = Postal Code. Note: other
            address details will be ignored if this value is missing
          </li>
          <li>
            Client 2 Address: Country Code (Optional) = Country code, for
            example "US". Note: other address details will be ignored if this
            value is missing.
          </li>
          <li>
            Client 2 Tax Identification Number Type (Optional) = Tax
            identification type code. Note: other tax identification details
            will be ignored if this value is missing. Valid options include:
            <ul className={styles.list}>
              <div className={styles.listWrapped}>
                <div>• SSN</div>
                <div>• ITIN</div>
                <div>• EIN</div>
              </div>
            </ul>
          </li>
          <li>
            Client 2 Tax Identification Number (Optional) = Example:
            "030-88-6000". Note: other tax identification details will be
            ignored if this value is missing
          </li>
          <li>
            Clients Advisor Group Id (Optional) = Advisor Group ID for primary &
            secondary holders. Example: "cd9e8590-e373-4fe3-9be6-d82d258ea76c".
            Clients cannot belong to more than one advisor group.
          </li>
        </ul>
      </li>

      <li>
        How to format the data
        <ul className={styles.list}>
          <li>
            Account Number
            <ul className={styles.list}>
              <li>Should not contain a hyphen, e.g. "11112244"</li>
            </ul>
          </li>

          <li>
            Phone Number
            <ul className={styles.list}>
              <li>
                Only digits in the following format: "(###) ###-####", e.g.
                "(987) 123-4567"
              </li>
            </ul>
          </li>

          <li>
            Date of birth
            <ul className={styles.list}>
              <li>Expected format: "MM/DD/YYYY", e.g. "04/20/1976"</li>
            </ul>
          </li>
        </ul>
      </li>

      <li>
        How to add a secondary account holder.
        <br />
        <br />
        All columns related to account holder are marked as optional, but you
        have to provide the following three essential values:
        <ul className={styles.list}>
          <li>Client 2 First Name</li>
          <li>Client 2 Last Name</li>
          <li>Client 2 Email Address</li>
        </ul>
        Lack of any of these values will result in skipping secondary account
        holder (client account will neither be created nor assigned to the
        account).
      </li>

      <li>
        How to update account holder data
        <br />
        <br />
        If the provided Account Number already exists in the system it will be
        considered as an update.
        <br />
        During the update the following rules apply:
        <ul className={styles.list}>
          <li>
            The input CSV stands for a desired state, in other words - what you
            see is what you get. An empty cell, with the exception of columns
            mentioned below, will result in clearing the field in our system.
            <br />
            <br />
            Updates on these columns can be done partially and new data will be
            merged with already existing information. Client's information is
            merged, that means that if client has multiple accounts and you want
            to update only address, you can enter this update only on one row.
            This also means that rule about ignoring partial address does not
            apply on update and you can change for example only Postal Code
            leaving the rest of the cells blank.
            <br />
            <br />
            Fields that will be merged with existing data (these columns are for
            Client 1 as well as Client 2):
            <ul className={styles.list}>
              <li>Gender</li>
              <li>Marital Status </li>
              <li>Phone Number</li>
              <li>Date of Birth</li>
              <li>Address: Street</li>
              <li>Address: Unit</li>
              <li>Address: City</li>
              <li>Address: State</li>
              <li>Address: Postal Code</li>
              <li>Address: Country Code</li>
              <li>Tax Identification Number Type</li>
              <li>Tax Identification Number</li>
            </ul>
            <br />
            To update Tax information both Tax Identification Number Type and
            Tax Identification Number must be provided.
          </li>
          <li>
            You cannot modify the following fields:
            <ul className={styles.list}>
              <li>Account Number</li>
              <li>Advisor</li>
              <li>Custodian</li>
              <li>Client 1 First Name</li>
              <li>Client 1 Last Name</li>
              <li>Client 1 Email Address</li>
              <li>Client 2 First Name</li>
              <li>Client 2 Last Name</li>
              <li>Client 2 Email Address</li>
            </ul>
            These values are used as identifiers for clients and accounts and
            may not be changed using this tool. Currently, only client
            information may be updated if using this tool to UPDATE (not upload
            new) data. Any changes to Advisor Group ID, Account Type, or Account
            Name will be ignored.
          </li>
        </ul>
      </li>
    </ul>
  );
};

export default Tips;
